import { hasPendingWorkspaceChanges } from './utils/workspaceGuard';
import { requestWorkerReply } from './utils/workerUpdateProtocol';
import { getAppUpdateState, getUpdateSupportMessage, runningAppVersion, setAppUpdateState } from './utils/appUpdateState';

const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;
let configured = false;
let reloadOnControllerChange = false;
let activatedUpdatePendingReload = false;
let reloadStarted = false;
let checkingPromise = null;

async function offerInstalledUpdate(registration) {
  const worker = registration?.waiting;
  if (!worker || worker.state !== 'installed') return;
  const version = await requestWorkerReply(worker, { type: 'GET_APP_VERSION' });
  // Tie both the prompt and dismissal to the installed worker, not a manifest
  // that might have been published before its assets finished downloading.
  if (registration.waiting !== worker || !version?.buildId || !version?.version) return;
  setAppUpdateState({ available: version, error: '' });
}

export function checkForAppUpdate(registrationOverride = null) {
  if (checkingPromise) return checkingPromise;
  setAppUpdateState({ checking: true, error: '' });
  checkingPromise = Promise.resolve().then(async () => {
    try {
      const unsupportedMessage = getUpdateSupportMessage();
      if (unsupportedMessage) {
        setAppUpdateState({ error: unsupportedMessage });
        return;
      }
      const registration = registrationOverride || await navigator.serviceWorker.getRegistration();
      if (!registration) {
        setAppUpdateState({ error: 'The app is still preparing updates. Please try again shortly.' });
        return;
      }
      // An already downloaded update remains usable offline.
      await offerInstalledUpdate(registration);
      if (navigator.onLine === false) {
        if (!getAppUpdateState().available) setAppUpdateState({ error: 'Connect to the internet to check for updates.' });
        return;
      }
      await registration.update();
      await offerInstalledUpdate(registration);
    } catch {
      if (!getAppUpdateState().available) setAppUpdateState({ error: 'Could not check for updates. Please try again when connected.' });
    } finally {
      setAppUpdateState({ checking: false, checked: true });
      checkingPromise = null;
    }
  });
  return checkingPromise;
}

export async function applyServiceWorkerUpdate(registrationOverride = null) {
  if (hasPendingWorkspaceChanges()) return 'blocked';
  if (activatedUpdatePendingReload) {
    window.location.reload();
    return 'activating';
  }
  if (!('serviceWorker' in navigator)) return 'unavailable';
  try {
    const registration = registrationOverride || await navigator.serviceWorker.getRegistration();
    const worker = registration?.waiting;
    if (worker?.state !== 'installed') return 'unavailable';
    if (hasPendingWorkspaceChanges()) return 'blocked';
    const response = await requestWorkerReply(worker, { type: 'APPLY_APP_UPDATE' }, 10000);
    return response?.result || 'failed';
  } catch {
    return 'failed';
  }
}

export async function updateAppNow() {
  if (getAppUpdateState().applying) return;
  setAppUpdateState({ applying: true, error: '' });
  const result = await applyServiceWorkerUpdate();
  setAppUpdateState({ applying: false, error: result === 'activating' ? '' : result === 'blocked'
    ? 'Save or discard pending edits and wait for saves in all CruisingOS tabs. Close any older or unresponsive tabs, then try again.'
    : 'The update could not be applied. Check for updates and try again.' });
  return result;
}

export function register(config) {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator) || configured) return;
  configured = true;
  let hadController = Boolean(navigator.serviceWorker.controller);
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'PREPARE_APP_UPDATE') {
      event.ports[0]?.postMessage({ safe: !hasPendingWorkspaceChanges() });
    }
    if (event.data?.type === 'COMMIT_APP_UPDATE') {
      reloadOnControllerChange = !hasPendingWorkspaceChanges();
      event.ports[0]?.postMessage({ safe: reloadOnControllerChange });
    }
    if (event.data?.type === 'CANCEL_APP_UPDATE') reloadOnControllerChange = false;
  });
  navigator.serviceWorker.addEventListener('controllerchange', async () => {
    if (!hadController) { hadController = true; return; }
    if (reloadStarted) return;
    activatedUpdatePendingReload = true;
    if (reloadOnControllerChange && !hasPendingWorkspaceChanges()) {
      reloadStarted = true;
      window.location.reload();
      return;
    }
    // An edit can start between the handshake and activation. Keep the draft
    // alive and let this tab reload later, even if another tab initiated it.
    reloadOnControllerChange = false;
    const version = await requestWorkerReply(navigator.serviceWorker.controller, { type: 'GET_APP_VERSION' });
    if (version?.buildId && version.buildId !== runningAppVersion.buildId) {
      setAppUpdateState({ available: version });
    }
  });
  const start = async () => {
    try {
      const registration = await navigator.serviceWorker.register(
        `${import.meta.env.BASE_URL || '/'}service-worker.js`, { updateViaCache: 'none' });
      const watchInstalling = () => {
        const worker = registration.installing;
        if (!worker) return;
        worker.addEventListener('statechange', () => {
          if (worker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              offerInstalledUpdate(registration);
              config?.onUpdate?.(registration);
            } else config?.onSuccess?.(registration);
          }
        });
      };
      registration.addEventListener('updatefound', watchInstalling);
      watchInstalling();
      const check = () => {
        if (document.visibilityState !== 'hidden') checkForAppUpdate(registration);
      };
      window.addEventListener('online', check);
      document.addEventListener('visibilitychange', check);
      window.setInterval(check, UPDATE_CHECK_INTERVAL_MS);
      check();
    } catch {
      setAppUpdateState({ error: 'Automatic updates are unavailable. Please reconnect and reopen the app.' });
    }
  };
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error("[serviceWorkerRegistration] Operation failed.");
      });
  }
}

export async function sendMessageToServiceWorker(message) {
  if (!('serviceWorker' in navigator)) return false;

  try {
    const registration = await navigator.serviceWorker.ready;
    const worker =
      navigator.serviceWorker.controller ||
      registration.active ||
      registration.waiting ||
      registration.installing;

    if (!worker) {
      return false;
    }

    worker.postMessage(message);
    return true;
  } catch (error) {
    console.error("Failed to send message to service worker");
    return false;
  }
}
